import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Plane, RotateCcw, Crown, Heart, Vote } from "lucide-react";
import { api, pad } from "../lib/api";
import { useTier } from "../lib/tiers";
import { PoolTracker } from "../components/PoolTracker";
import { TierSwitch } from "../components/TierSwitch";
import { TierPlans } from "../components/TierPlans";
import { FoundersBanner } from "../components/FoundersBanner";
import { TermsSummary } from "../components/TermsSummary";
import { WinnerReviews } from "../components/WinnerReviews";

const HERO = "https://images.unsplash.com/photo-1515404929826-76fff9fef6fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const steps = [
  { icon: Users, title: "Pick your line", text: "Domestic, International, Family, Accessible or Young Children. Join one or several — each has its own private place in line." },
  { icon: Plane, title: "Payments fund trips", text: "85% of every payment flows into that line's trip pool. Pay extra anytime to upgrade your own future trip." },
  { icon: Crown, title: "Accept, pass or give", text: "At #001 you get 7 days to accept, pass (move back 3 spots), or donate the full trip value to trade-school scholarships." },
  { icon: Heart, title: "Built to give back", text: "We support students in the skilled trades. Read how on our Mission page." },
  { icon: Vote, title: "Members vote", text: "Vacations, concerts and experiences — paid-up members of each line vote on its next reward." },
  { icon: RotateCcw, title: "Stay paid, stay in line", text: "Miss a payment and you drop off the list. Renew later and you rejoin at the very back. Autopay saves 10%." },
];

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [tier, setTier] = useTier();
  useEffect(() => { api.get("/public/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => { api.get(`/queue?tier=${tier}&limit=5`).then((r) => setQueue(r.data.items)); }, [tier]);
  const ts = stats?.[tier];

  return (
    <div>
      <section className="relative min-h-[92vh] flex items-center grain overflow-hidden">
        <img src={HERO} alt="Overwater villa" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-[#0B0F19]/75 to-[#0B0F19]/20" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0B0F19] to-transparent" />
        <div className="blob blob-a w-80 h-80 -left-20 top-24" />
        <div className="blob blob-c w-72 h-72 left-1/3 bottom-0" />
        <div className="blob blob-b w-96 h-96 -right-24 top-10" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-16 grid lg:grid-cols-12 gap-10 w-full">
          <div className="lg:col-span-7 rise">
            <div className="flex flex-wrap items-center gap-3">
              <p className="eyebrow">Turn-based · Crowd-funded · Travel</p>
              <Link to="/register" data-testid="hero-founder-sticker" className="sticker">Founding 500 · 25% off</Link>
            </div>
            <h1 className="font-serif-lux mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Your turn in paradise <span className="text-gradient">is already in line.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl">
              A members' club where every subscription funds the next all-expense-paid trip — awarded strictly in order of who joined first.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-join-btn" className="btn-gold px-7 py-3.5 inline-flex items-center gap-2">
                Join from $29/mo <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/queue" data-testid="hero-queue-btn" className="btn-ghost px-7 py-3.5">See the live lines</Link>
            </div>
          </div>
          <div className="lg:col-span-5 lg:pt-16 space-y-4 rise" style={{ animationDelay: "0.2s" }}>
            <TierSwitch value={tier} onChange={setTier} className="!gap-1.5 [&>button]:text-xs [&>button]:px-3 [&>button]:py-1.5" />
            <PoolTracker stats={ts} />
            <div className="lux-card p-6 bg-[#1E263E]/80 backdrop-blur-xl">
              <div className="flex justify-between items-center">
                <p className="eyebrow">Front of the line · aliases only</p>
                <span data-testid="hero-active-count" className="font-mono-lux text-xs text-slate-400">{ts?.active_members ?? 0} active</span>
              </div>
              <ul className="mt-4 space-y-2">
                {queue.map((q) => (
                  <li key={q.position} className="flex items-center justify-between text-sm">
                    <span className="font-mono-lux text-[#38BDF8]">{pad(q.position)}</span>
                    <span className={q.is_me ? "text-[#38BDF8]" : "text-slate-300"}>{q.is_me ? "You" : q.alias}</span>
                  </li>
                ))}
                {queue.length === 0 && <li className="text-sm text-slate-500">Be the first in line.</li>}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-10 py-24">
        <p className="eyebrow">How the line works</p>
        <h2 className="font-serif-lux text-3xl md:text-4xl mt-3 max-w-2xl">Fair by design. Nobody skips ahead.</h2>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i}`} className="lux-card p-7 hover:-translate-y-1">
              <p className="font-mono-lux text-xs text-slate-500">0{i + 1}</p>
              <s.icon className="w-6 h-6 text-[#38BDF8] mt-6" />
              <h3 className="font-serif-lux text-2xl mt-4">{s.title}</h3>
              <p className="text-sm text-slate-400 mt-3 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      <FoundersBanner />
      <WinnerReviews />
      <TierPlans stats={stats} />

      {ts?.next_vacation && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pb-28">
          <div className="relative rounded-2xl overflow-hidden grid md:grid-cols-2 border border-white/10">
            <img src={ts.next_vacation.image_url} alt={ts.next_vacation.title} className="h-72 md:h-full w-full object-cover" />
            <div className="p-10 md:p-14 bg-[#13192B]">
              <p className="eyebrow">{ts.label} · leading the vote</p>
              <h3 data-testid="next-vacation-title" className="font-serif-lux text-4xl mt-4">{ts.next_vacation.title}</h3>
              <p className="text-slate-400 mt-2">{ts.next_vacation.location} · {ts.next_vacation.nights} nights</p>
              <ul className="mt-6 space-y-2 text-sm text-slate-300">
                {ts.next_vacation.includes.map((x) => <li key={x}>— {x}</li>)}
              </ul>
              <Link to="/vacations" data-testid="see-vacations-btn" className="btn-ghost inline-block mt-8 px-6 py-3 text-sm">Browse all destinations</Link>
            </div>
          </div>
        </section>
      )}
      <TermsSummary />
    </div>
  );
}
